import React, { useState, useEffect } from 'react';
import ProductCard from '../components/ProductCard';
import { supabase } from '../supabaseClient';
import Hero from '../components/hero';
import FeaturedHighlights from '../components/FeaturedHighlights';
import PurchaseBenefits from '../components/PurchaseBenefits';
import BlogPreview from '../components/BlogPreview';

const HomePage = ({ onAddToCart, onProductClick, onNavigate }) => {
  // Productos completos traídos desde Supabase
  const [products, setProducts] = useState([]);

  // IDs de productos destacados (site_assets.kind === 'featured_product')
  const [featuredIds, setFeaturedIds] = useState([]);

  // Mapas slug por id
  const [categoryMap, setCategoryMap] = useState({});
  const [subcatMap, setSubcatMap] = useState({});

  /* =========================
   * Navegación segura SPA
   * ========================= */
  const safeNavigate = (path) => {
    if (typeof onNavigate === 'function') {
      onNavigate(path);
    } else {
      window.location.assign(path);
    }
  };

  const goToCategory = (catSlug) => {
    safeNavigate(`/${encodeURIComponent(catSlug)}`);
  };

  /* =========================
   * 1. Taxonomía
   * ========================= */
  useEffect(() => {
    const fetchTaxonomy = async () => {
      try {
        const { data: cats, error: catsErr } = await supabase
          .from('categories')
          .select('id, slug, is_active')
          .eq('is_active', true);

        if (catsErr) {
          console.error('Error cargando categories:', catsErr.message);
        }

        const { data: subs, error: subsErr } = await supabase
          .from('subcategories')
          .select('id, slug, is_active')
          .eq('is_active', true);

        if (subsErr) {
          console.error('Error cargando subcategories:', subsErr.message);
        }

        const catMapTmp = {};
        (cats || []).forEach((c) => {
          if (c && c.id != null) catMapTmp[c.id] = c.slug;
        });

        const subMapTmp = {};
        (subs || []).forEach((s) => {
          if (s && s.id != null) subMapTmp[s.id] = s.slug;
        });

        setCategoryMap(catMapTmp);
        setSubcatMap(subMapTmp);
      } catch (err) {
        console.error('Error inesperado cargando taxonomy:', err);
      }
    };

    fetchTaxonomy();
  }, []);

  /* =========================
   * 2. Productos
   * ========================= */
  useEffect(() => {
    const fetchProducts = async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error al obtener productos:', error);
        setProducts([]);
      } else {
        setProducts(data || []);
      }
    };

    fetchProducts();
  }, []);

  /* =========================
   * 3. site_assets (solo para destacados)
   * ========================= */
  useEffect(() => {
    const fetchAssets = async () => {
      const { data: assets, error } = await supabase
        .from('site_assets')
        .select('kind, ref_value, position, active, starts_at, ends_at')
        .eq('active', true)
        .order('position', { ascending: true });

      if (error) {
        console.error('Error al obtener site_assets:', error);
        return;
      }

      const now = new Date();
      const inSchedule = (row) => {
        const startOk = !row.starts_at || new Date(row.starts_at) <= now;
        const endOk = !row.ends_at || new Date(row.ends_at) >= now;
        return startOk && endOk;
      };

      const filtered = (assets || []).filter(inSchedule);

      // Productos destacados
      const feats = filtered
        .filter((a) => a.kind === 'featured_product')
        .map((a) => Number(a.ref_value))
        .filter(Number.isFinite);

      setFeaturedIds(feats);
    };

    fetchAssets();
  }, []);

  /* =========================
   * 4. Productos destacados reales
   * ========================= */
  const featuredProducts = products.filter((p) =>
    featuredIds.includes(p.id)
  );

  /* =========================
   * 5. URL base bonita p/ destacado
   * ========================= */
  const getBasePathForProduct = (p) => {
    const catSlug = categoryMap[p.category_id];
    const subSlug = subcatMap[p.subcategory_id];

    if (catSlug && subSlug) {
      return `/${catSlug}/${subSlug}`;
    }
    if (catSlug) {
      return `/${catSlug}`;
    }
    return '/tecnologia';
  };

  /* =========================================================================
   * RENDER PÁGINA
   * ========================================================================= */
  return (
    <div className="w-full">
      <Hero />

      {/* ================= BANNERS DESTACADOS TOP ================= */}
      <FeaturedHighlights onNavigate={safeNavigate} />

      {/* ================= CATEGORÍAS PRINCIPALES ================= */}
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <section className="mb-20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">
              Explora Nuestras Categorías
            </h2>
            <div className="flex flex-col md:flex-row justify-center items-center gap-10">
              {/* Perfumes */}
              <div
                data-aos="fade-up"
                data-aos-duration="800"
                className="shine w-[90%] sm:w-[300px] md:w-[400px] lg:w-[300px] aspect-square rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-transform duration-300 hover:scale-105 cursor-pointer"
                onClick={() => goToCategory('perfumes')}
              >
                <img
                  src="https://jszgeoosgpozjlvohrjy.supabase.co/storage/v1/object/public/assets//perfumescat2.png"
                  alt="Perfumes"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Tecnología */}
              <div
                data-aos="fade-up"
                data-aos-delay="200"
                data-aos-duration="800"
                className="shine w-[90%] sm:w-[300px] md:w-[400px] lg:w-[300px] aspect-square rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-transform duration-300 hover:scale-105 cursor-pointer"
                onClick={() => goToCategory('tecnologia')}
              >
                <img
                  src="https://jszgeoosgpozjlvohrjy.supabase.co/storage/v1/object/public/assets//techcat2.png"
                  alt="Tecnología"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Hogar */}
              <div
                data-aos="fade-up"
                data-aos-delay="200"
                data-aos-duration="800"
                className="shine w-[90%] sm:w-[300px] md:w-[400px] lg:w-[300px] aspect-square rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-transform duration-300 hover:scale-105 cursor-pointer"
                onClick={() => goToCategory('hogar')}
              >
                <img
                  src="https://jszgeoosgpozjlvohrjy.supabase.co/storage/v1/object/public/assets//hogar.png"
                  alt="Hogar"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ================= PRODUCTOS DESTACADOS ================= */}
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 text-center">
          Productos Destacados
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-4 sm:gap-6">
          {featuredProducts.map((p) => {
            const basePath = getBasePathForProduct(p);
            return (
              <ProductCard
                key={p.id}
                product={p}
                onAddToCart={onAddToCart}
                onProductClick={() => {
                  onProductClick(p.id, basePath);
                }}
              />
            );
          })}
        </div>

        <br className="my-12" />

        {/* ================= BANNER PROMO ANCHO ================= */}
        <section className="w-full mb-[60px]">
          <div className="container mx-auto px-4">
            <div className="shine overflow-hidden">
              <div data-aos="fade-right" className="w-full">
                <img
                  src="https://jszgeoosgpozjlvohrjy.supabase.co/storage/v1/object/public/assets//banner-4.png"
                  alt="Promoción destacada"
                  className="w-full h-auto sm:h-full object-cover rounded-xl shadow-md"
                />
              </div>
            </div>
          </div>
        </section>

        <PurchaseBenefits />
      </div>
      <BlogPreview onNavigate={safeNavigate} />
    </div>
  );
};

export default HomePage;
